import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_HIDE_INACTIVE_ENTITIES_ENABLED,
} from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { useRouteMatch } from '../../../util/useRouteMatch';
import {
	getPolicySegments,
	getPolicyStatus,
	getTodaysPolicySegment,
} from '../../../util/util';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import FieldLabel from '../../fields/FieldLabel';
import TreeNode, {
	TreeData,
	TreeItemData,
} from '../../layouts/SidePanel/TreeNode';
import { getCurrentGraceLapse } from '../../search/util';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';
import IssuedPolicyTree from './IssuedPolicyTree';

export interface PolicyRoutes {
	AUX_DATA: string;
	CANCELLATIONS: string;
	CLAIMS: string;
	DOCUMENTS: string;
	ENDORSEMENTS: string;
	EXPOSURE: string;
	EXPOSURES: string;
	FEES: string;
	FUTURE_INVOICES: string;
	GRACE_PERIODS: string;
	HISTORICAL_QUOTES: string;
	INVOICE: string;
	INVOICES: string;
	PERIL: string;
	PERILS: string;
	POLICY_DETAILS: string;
	POLICY_HISTORY: string;
	PREMIUM_REPORTS: string;
	PRICING: string;
	RENEWALS: string;
	ROOT: string;
	PAYMENTS: string;
	NEW_PAYMENT: string;
	PAYMENT: string;
	EDIT_PAYMENT: string;
	DISBURSEMENTS: string;
	DISBURSEMENT: string;
	NEW_DISBURSEMENT: string;
	EDIT_DISBURSEMENT: string;
	CATCHUPS: string;
	CATCHUP: string;
	NEW_CATCHUP: string;
}

export const POLICY_ROUTES: PolicyRoutes = {
	AUX_DATA: '/policy/:policyId/auxData',
	CANCELLATIONS: '/policy/:policyId/cancellations',
	CLAIMS: '/policy/:policyId/claims',
	DOCUMENTS: '/policy/:policyId/documents',
	ENDORSEMENTS: '/policy/:policyId/endorsements',
	EXPOSURE: '/policy/:policyId/exposures/:exposureId',
	EXPOSURES: '/policy/:policyId/exposures',
	FEES: '/policy/:policyId/fees',
	FUTURE_INVOICES: '/policy/:policyId/futureInvoices',
	GRACE_PERIODS: '/policy/:policyId/gracePeriods',
	HISTORICAL_QUOTES: '/policy/:policyId/historicalQuotes',
	INVOICE: '/policy/:policyId/invoices/:invoiceId',
	INVOICES: '/policy/:policyId/invoices',
	PERIL: '/policy/:policyId/exposures/:exposureId/perils/:perilId',
	PERILS: '/policy/:policyId/exposures/:exposureId/perils',
	POLICY_DETAILS: '/policy/:policyId/details',
	POLICY_HISTORY: '/policy/:policyId/policyHistory',
	PREMIUM_REPORTS: '/policy/:policyId/premiumReports',
	PRICING: '/policy/:policyId/pricing',
	RENEWALS: '/policy/:policyId/renewals',
	ROOT: '/policy/:policyId',
	PAYMENTS: '/policy/:policyId/payments',
	NEW_PAYMENT: '/policy/:policyId/payments/new',
	PAYMENT: '/policy/:policyId/payments/:paymentLocator',
	EDIT_PAYMENT: '/policy/:policyId/payments/:paymentLocator/edit',
	DISBURSEMENTS: '/policy/:policyId/disbursements',
	NEW_DISBURSEMENT: '/policy/:policyId/disbursements/new',
	DISBURSEMENT: '/policy/:policyId/disbursements/:disbursementLocator',
	EDIT_DISBURSEMENT:
		'/policy/:policyId/disbursements/:disbursementLocator/edit',
	CATCHUPS: '/policy/:policyId/catchUps',
	NEW_CATCHUP: '/policy/:policyId/catchUps/new',
	CATCHUP: '/policy/:policyId/catchUps/:catchUpLocator',
};

interface Props {
	policyData: PolicyData;
	errorData: ErrorData;
	authData: AuthData;
}

const PolicySidebar = (props: Props) => {
	const { t } = useTranslation();
	const { policyData, errorData, authData } = props;
	const { hasPermission } = authData;
	const { reset } = useFormContext();
	const { currentErrorObject } = errorData;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const hideInactiveEntities =
		authData?.auth?.configProperties?.[UI_HIDE_INACTIVE_ENTITIES_ENABLED];

	const {
		policy,
		graceLapse,
		selectedQuote,
		policyViewTimestamp,
		setPolicyViewTimestamp,
		productConfig,
		updateFields,
	} = policyData;

	const routeMatch = useRouteMatch(
		Object.keys(POLICY_ROUTES).map(
			(key) => POLICY_ROUTES[key as keyof PolicyRoutes],
		),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';

	const policySegmentOptions = getPolicySegments(policy, authData);
	const selectedPolicySegment = getTodaysPolicySegment(
		policySegmentOptions,
		policyViewTimestamp,
	);
	const [itemsExpanded, setItemsExpanded] = useState([]);

	// non-quote policy has policy state
	const policyState = getPolicyStatus(policy, getCurrentGraceLapse(graceLapse));
	const isCancellationsEnabled =
		hasPermission('cancellation.fetch') &&
		productConfig &&
		productConfig.policyConfiguration.cancellations &&
		productConfig.policyConfiguration.cancellations.reasons;

	const getInitialExpandedItems = () => {
		const ids = ['Exposures'];
		// if looking at a certain exposure, expand that exposure and it's perils
		if (policyData.selectedExposure) {
			ids.push(policyData.selectedExposure.locator);
			ids.push(`${policyData.selectedExposure.locator}-Perils`);
		}

		if (params.paymentLocator) {
			return ['Payments'];
		}

		return ids;
	};

	useEffect(() => {
		const initialIds = getInitialExpandedItems();
		setItemsExpanded(initialIds);
	}, [policyData.selectedExposure, params.invoiceId, params.paymentLocator]);

	const policyLink = `/policy/${policy.locator}`;
	const priceBreakdownLink = `${policyLink}/pricing`;
	const documentsLink = `${policyLink}/documents`;
	const invoicesLink = `${policyLink}/invoices`;
	const futureInvoicesLink = `${policyLink}/futureInvoices`;
	const historicalQuotesLink = `${policyLink}/historicalQuotes`;
	const claimsLink = `${policyLink}/claims`;
	const cancellationsLink = `${policyLink}/cancellations`;
	const premiumReportsLink = `${policyLink}/premiumReports`;
	const endorsementsLink = `${policyLink}/endorsements`;
	const renewalsLink = `${policyLink}/renewals`;
	const feesLink = `${policyLink}/fees`;
	const gracePeriodsLink = `${policyLink}/gracePeriods`;
	const showAcceptedLinks =
		policy.issuedTimestamp ||
		(!selectedQuote && policyState !== 'Application In Progress');
	const policyHistoryLink = `${policyLink}/policyHistory`;
	const paymentsLink = `${policyLink}/payments`;
	const disbursementsLink = `${policyLink}/disbursements`;
	const catchUpsLink = `${policyLink}/catchUps`;

	const getRootItem = () => {
		const children = ['Policy Details', 'Exposures', 'Price Breakdown'];

		if (showAcceptedLinks) {
			children.push('Documents');
			if (hasPermission('invoice.fetch')) {
				children.push('Invoices');
			}
			children.push('Future Invoices');

			if (cashManagementEnabled && hasPermission('payment.fetch')) {
				children.push('Payments');
			}

			if (cashManagementEnabled && hasPermission('disbursement.fetch')) {
				children.push('Disbursements');
			}
			if (cashManagementEnabled && hasPermission('catchUpInvoice.fetch')) {
				children.push('Catch Ups');
			}

			if (isCancellationsEnabled) {
				children.push('Cancellations');
			}

			if (hasPermission('claim.fetch')) {
				children.push('Claims');
			}

			if (hasPermission('endorsement.fetch')) {
				children.push('Endorsements');
			}

			if (hasPermission('renewal.fetch')) {
				children.push('Renewals');
			}

			if (selectedQuote) {
				children.push('Historical Quotes');
			}
			children.push('Policy History');
		}

		if (
			policy.issuedTimestamp &&
			policy.premiumReportName &&
			hasPermission('premiumReport.fetch')
		) {
			children.push('Premium Reports');
		}

		if (policy.issuedTimestamp && !policy.premiumReportName) {
			children.push('Fees');
		}

		if (policy.issuedTimestamp && hasPermission('gracePeriod.fetch')) {
			children.push('Grace Periods');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		};
	};

	const getItems = () => {
		const policyTree = IssuedPolicyTree(
			policyData,
			itemsExpanded,
			currentErrorObject,
			hideInactiveEntities,
		);

		let items: TreeItemData = {
			root: getRootItem(),
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === POLICY_ROUTES.PRICING,
				},
			},
			...policyTree,
		};

		if (showAcceptedLinks) {
			const acceptedSidebar: any = {
				Documents: {
					id: 'Documents',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Documents',
						link: documentsLink,
						selected: routeName === POLICY_ROUTES.DOCUMENTS,
					},
				},
				Invoices: {
					id: 'Invoices',
					children: [],
					hasChildren: false,
					isExpanded: false,
					isChildrenLoading: false,
					data: {
						title: 'Invoices',
						link: invoicesLink,
						selected:
							routeName === POLICY_ROUTES.INVOICES ||
							routeName === POLICY_ROUTES.INVOICE,
					},
				},
				'Future Invoices': {
					id: 'Future Invoices',
					children: [],
					hasChildren: false,
					isExpanded: false,
					isChildrenLoading: false,
					data: {
						title: 'Future Invoices',
						link: futureInvoicesLink,
						selected: routeName === POLICY_ROUTES.FUTURE_INVOICES,
					},
				},
				Claims: {
					id: 'Claims',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Claims',
						link: claimsLink,
						selected: routeName === POLICY_ROUTES.CLAIMS,
					},
				},
				Endorsements: {
					id: 'Endorsements',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: t('endorsement.plural.upper'),
						link: endorsementsLink,
						selected: routeName === POLICY_ROUTES.ENDORSEMENTS,
					},
				},
				Renewals: {
					id: 'Renewals',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Renewals',
						link: renewalsLink,
						selected: routeName === POLICY_ROUTES.RENEWALS,
					},
				},
				Fees: {
					id: 'Fees',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Fees',
						link: feesLink,
						selected: routeName === POLICY_ROUTES.FEES,
					},
				},
				'Grace Periods': {
					id: 'Grace Periods',
					children: [],
					hasChildren: false,
					isExpanded: false,
					isChildrenLoading: false,
					data: {
						title: 'Grace Periods',
						link: gracePeriodsLink,
						selected: routeName === POLICY_ROUTES.GRACE_PERIODS,
					},
				},
				'Policy History': {
					id: 'Policy History',
					children: [],
					hasChildren: false,
					isExpanded: false,
					isChildrenLoading: false,
					data: {
						title: 'Policy History',
						link: policyHistoryLink,
						selected: routeName === POLICY_ROUTES.POLICY_HISTORY,
					},
				},
			};
			if (isCancellationsEnabled) {
				acceptedSidebar.Cancellations = {
					id: 'Cancellations',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Cancellations',
						link: cancellationsLink,
						selected: routeName === POLICY_ROUTES.CANCELLATIONS,
					},
				};
			}

			if (policy.issuedTimestamp && selectedQuote) {
				items['Historical Quotes'] = {
					id: 'Historical Quotes',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Historical Quotes',
						link: historicalQuotesLink,
						selected: routeName === POLICY_ROUTES.HISTORICAL_QUOTES,
					},
				};
			}

			items = { ...items, ...acceptedSidebar };
		}

		if (policy.issuedTimestamp && policy.premiumReportName) {
			const premiumReport: TreeItemData = {
				'Premium Reports': {
					id: 'Premium Reports',
					children: [],
					hasChildren: false,
					isExpanded: true,
					isChildrenLoading: false,
					data: {
						title: 'Premium Reports',
						link: premiumReportsLink,
						selected: routeName === POLICY_ROUTES.PREMIUM_REPORTS,
					},
				},
			};

			items = { ...items, ...premiumReport };
		}

		if (cashManagementEnabled) {
			items['Payments'] = {
				id: 'Payments',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Payments',
					link: paymentsLink,
					selected:
						routeName === POLICY_ROUTES.PAYMENTS ||
						routeName === POLICY_ROUTES.NEW_PAYMENT ||
						routeName === POLICY_ROUTES.EDIT_PAYMENT ||
						routeName === POLICY_ROUTES.PAYMENT,
				},
			};
		}

		if (cashManagementEnabled) {
			items['Disbursements'] = {
				id: 'Disbursements',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Disbursements',
					link: disbursementsLink,
					selected:
						routeName === POLICY_ROUTES.DISBURSEMENTS ||
						routeName === POLICY_ROUTES.NEW_DISBURSEMENT ||
						routeName === POLICY_ROUTES.DISBURSEMENT ||
						routeName === POLICY_ROUTES.EDIT_DISBURSEMENT,
				},
			};
			items['Catch Ups'] = {
				id: 'Catch Ups',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Catch-Ups',
					link: catchUpsLink,
					selected:
						routeName === POLICY_ROUTES.CATCHUPS ||
						routeName === POLICY_ROUTES.CATCHUP ||
						routeName === POLICY_ROUTES.NEW_CATCHUP,
				},
			};
		}

		return {
			...items,
		};
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItems(),
		};

		return sidePanelData;
	};
	const tree = initializeTree();

	const updateViewTimestamp = async (opt: SelectValue) => {
		await setPolicyViewTimestamp(opt.value);
		const freshPolicy = await updateFields();
		const defaultPolicyValues = getDefaultPolicyValues(
			freshPolicy.fields,
			props.authData,
		);
		reset(defaultPolicyValues);
	};

	const renderPolicyVersionSelector = () => {
		return (
			<div style={{ width: '220px', margin: '15px', fontSize: '14px' }}>
				<FieldLabel
					title='View Policy On'
					id={'view-policy-on-selector-label'}
					style={{ fontSize: '12px' }}
				/>
				<ReactSelect
					id='view-policy-on-selector'
					options={policySegmentOptions}
					value={selectedPolicySegment}
					onChange={(opt: SelectValue) => updateViewTimestamp(opt)}
					styles={{
						valueContainer: (provided: any, state: any) => ({
							...provided,
							paddingLeft: '0px',
							fontSize: '14px',
						}),
					}}
				/>
			</div>
		);
	};

	return (
		<>
			{renderPolicyVersionSelector()}
			<TreeNode treeData={tree} defaultExpanded={itemsExpanded} />
		</>
	);
};

export default withPolicyContext<Props>(PolicySidebar);
